import { useState, useMemo, useEffect } from "react";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { ColorModeContext } from "./ColorModeContext";
import { useTranslation } from "react-i18next";
import { createEmotionCache } from "./emotionCache";
import { CacheProvider } from "@emotion/react";
import {
  N, ND, BLUR, BLUR_SM, PAGE_BG, PAGE_BG_DARK,
  raise, raiseMd, raiseSm, inset, insetSm,
  dRaise, dRaiseMd, dRaiseSm, dInset, dInsetSm,
} from "../styles/neu";

// Teleredo brand palette — must match tokens.css exactly
const PURPLE   = "#7A3FE4"; // --brand-violet
const PURPLE_L = "#9A6BF7";
const PURPLE_D = "#6330D9";
const TEAL     = "#1FC0DA"; // --brand-cyan
const DANGER   = "#EF4444";
const SUCCESS  = "#22C55E";
const WARNING  = "#F59E0B";

export default function ThemeContextProvider({ children }) {
  const [mode, setMode] = useState("light");
  const { i18n } = useTranslation();
  // Derive RTL from i18n.language so the cache+theme stay in sync on every lang change.
  // ar is the only RTL language in the supported set.
  const isRtl = i18n.language === "ar";
  const cache = useMemo(() => createEmotionCache(isRtl), [isRtl]);

  const toggleColorMode = () => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  };

  const theme = useMemo(() => {
    const isLight = mode === "light";
    const T = isLight ? N : ND;
    const sh = isLight
      ? { raise, raiseMd, raiseSm, inset, insetSm }
      : { raise: dRaise, raiseMd: dRaiseMd, raiseSm: dRaiseSm, inset: dInset, insetSm: dInsetSm };
    const primary = isLight ? PURPLE : PURPLE_L;

    return createTheme({
      direction: i18n.language === "ar" ? "rtl" : "ltr",
      palette: {
        mode,
        primary: {
          main:         primary,
          light:        "#B794FF",
          dark:         PURPLE_D,
          contrastText: "#ffffff",
        },
        secondary: {
          main:         TEAL,
          light:        "#4DD9D9",
          dark:         "#00A5A5",
          contrastText: "#101828",
        },
        error:   { main: DANGER,   light: "#FCA5A5", dark: "#B91C1C" },
        warning: { main: WARNING,  light: "#FDE68A", dark: "#B45309" },
        success: { main: SUCCESS,  light: "#86EFAC", dark: "#15803D" },
        info:    { main: "#3B82F6" },
        background: {
          default: isLight ? PAGE_BG      : PAGE_BG_DARK,
          paper:   T.bg,
        },
        text: {
          primary:   T.text,
          secondary: T.sub,
          disabled:  T.muted,
        },
        divider: T.border,
      },
      typography: {
        fontFamily: `'Plus Jakarta Sans', system-ui, -apple-system, sans-serif`,
        fontWeightLight:   300,
        fontWeightRegular: 400,
        fontWeightMedium:  500,
        fontWeightBold:    700,
        h1: { fontWeight: 800, letterSpacing: "-0.03em",  lineHeight: 1.15 },
        h2: { fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.2  },
        h3: { fontWeight: 700, letterSpacing: "-0.02em",  lineHeight: 1.25 },
        h4: { fontWeight: 600, letterSpacing: "-0.015em", lineHeight: 1.3  },
        h5: { fontWeight: 600, letterSpacing: "-0.01em",  lineHeight: 1.4  },
        h6: { fontWeight: 600, letterSpacing: "-0.005em", lineHeight: 1.5  },
        subtitle1: { fontWeight: 500, lineHeight: 1.6 },
        subtitle2: { fontWeight: 500, fontSize: "0.8125rem", lineHeight: 1.57 },
        body1: { lineHeight: 1.65, letterSpacing: "-0.005em" },
        body2: { lineHeight: 1.6,  fontSize: "0.875rem" },
        button: { fontWeight: 600, textTransform: "none", letterSpacing: "-0.01em" },
        caption: { fontSize: "0.75rem", lineHeight: 1.5, letterSpacing: "0" },
        overline: { fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", fontSize: "0.6875rem" },
      },
      shape: { borderRadius: 12 },
      shadows: [
        "none",
        sh.raiseSm, sh.raiseSm, sh.raiseSm, sh.raiseSm,
        sh.raiseSm, sh.raiseMd, sh.raiseMd, sh.raiseMd,
        sh.raiseMd, sh.raise,   sh.raise,   sh.raise,
        sh.raise,   sh.raise,   sh.raise,   sh.raise,
        sh.raise,   sh.raise,   sh.raise,   sh.raise,
        sh.raise,   sh.raise,   sh.raise,   sh.raise,
      ],
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            html: { height: "100%" },
            body: {
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              background: isLight ? PAGE_BG : PAGE_BG_DARK,
              minHeight: "100%",
            },
            "#root": { minHeight: "100vh" },
            "*, *::before, *::after": { boxSizing: "border-box" },
          },
        },

        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 10,
              transition: "all 0.18s cubic-bezier(.2,.8,.2,1)",
              "&:active": { transform: "scale(0.97)" },
            },
            containedPrimary: {
              background: `linear-gradient(135deg, ${TEAL} 0%, ${PURPLE} 100%)`,
              color: "#fff",
              boxShadow: `0 2px 8px ${alpha(PURPLE, 0.35)}, 0 1px 2px ${alpha(PURPLE, 0.2)}`,
              border: "none",
              "&:hover": {
                background: `linear-gradient(135deg, #00A5A5 0%, ${PURPLE_D} 100%)`,
                boxShadow: `0 4px 16px ${alpha(PURPLE, 0.45)}, 0 2px 6px ${alpha(PURPLE, 0.25)}`,
                transform: "translateY(-1px)",
              },
              "&:active": { transform: "scale(0.97)", boxShadow: `0 1px 4px ${alpha(PURPLE, 0.3)}` },
            },
            containedSecondary: {
              background: `linear-gradient(135deg, ${TEAL} 0%, #4DD9D9 100%)`,
              color: "#101828",
              boxShadow: `0 2px 8px ${alpha(TEAL, 0.35)}`,
              border: "none",
              "&:hover": {
                background: `linear-gradient(135deg, #00A5A5 0%, ${TEAL} 100%)`,
                boxShadow: `0 4px 16px ${alpha(TEAL, 0.45)}`,
                transform: "translateY(-1px)",
              },
            },
            contained: {
              background: "var(--surface-card)",
              color: T.text,
              border: "1px solid var(--line-200)",
              boxShadow: "none",
              "&:hover": {
                background: "var(--surface-2)",
                boxShadow: "var(--shadow-card)",
                transform: "translateY(-1px)",
              },
            },
            outlined: {
              borderWidth: "1.5px",
              "&:hover": { borderWidth: "1.5px" },
            },
            outlinedPrimary: {
              borderColor: alpha(primary, 0.45),
              "&:hover": {
                borderColor: primary,
                background: alpha(primary, 0.04),
                borderWidth: "1.5px",
              },
            },
            outlinedError: {
              borderColor: alpha(DANGER, 0.5),
              color: DANGER,
              "&:hover": { background: alpha(DANGER, 0.05), borderColor: DANGER },
            },
            sizeSmall:  { padding: "5px 14px",  fontSize: "0.8125rem", borderRadius: 8  },
            sizeMedium: { padding: "8px 20px",   borderRadius: 10 },
            sizeLarge:  { padding: "12px 28px",  fontSize: "1rem", borderRadius: 12 },
          },
        },

        MuiIconButton: {
          styleOverrides: {
            root: {
              borderRadius: 10,
              transition: "all 0.15s ease",
              "&:hover": { background: alpha(T.text, 0.06) },
            },
            sizeSmall: { borderRadius: 8 },
          },
        },

        MuiCard: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              backgroundImage: "none",
              background: "var(--surface-card)",
              border: "1px solid var(--line-200)",
              boxShadow: "var(--shadow-card)",
              borderRadius: 18,
              transition: "box-shadow 0.22s ease, transform 0.22s ease",
              "&:hover": { boxShadow: "var(--shadow-card)" },
            },
          },
        },

        MuiCardContent: {
          styleOverrides: {
            root: { "&:last-child": { paddingBottom: 16 } },
          },
        },

        MuiPaper: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              backgroundImage: "none",
              background: "var(--surface-card)",
            },
            elevation1: { boxShadow: "none", border: "1px solid var(--line-200)" },
            elevation2: { boxShadow: "var(--shadow-card)", border: "1px solid var(--line-200)" },
            elevation3: { boxShadow: "var(--shadow-card)",   border: "1px solid var(--line-200)" },
          },
        },

        MuiTextField: {
          defaultProps: { size: "medium" },
          styleOverrides: {
            root: {
              "& .MuiOutlinedInput-root": {
                background: "var(--surface-card)",
                borderRadius: 10,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: T.border,
                  borderWidth: "1.5px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha(primary, 0.5),
                },
                "&.Mui-focused": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: primary,
                    borderWidth: "2px",
                  },
                  boxShadow: `0 0 0 3px ${alpha(primary, 0.1)}`,
                },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: DANGER,
                },
              },
            },
          },
        },

        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              background: "var(--surface-card)",
              borderRadius: 10,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: T.border,
                borderWidth: "1.5px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: alpha(primary, 0.5),
              },
              "&.Mui-focused": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: primary,
                  borderWidth: "2px",
                },
                boxShadow: `0 0 0 3px ${alpha(primary, 0.1)}`,
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: DANGER,
              },
            },
          },
        },

        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: T.sub,
              fontWeight: 500,
              "&.Mui-focused": { color: primary },
              "&.Mui-error":   { color: DANGER  },
            },
          },
        },

        MuiSelect: {
          styleOverrides: {
            outlined: { borderRadius: 10 },
          },
        },

        MuiFormHelperText: {
          styleOverrides: {
            root: { fontSize: "0.75rem", fontWeight: 500, marginTop: 4 },
          },
        },

        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: 10,
              transition: "all 0.18s ease",
              "&:hover": { background: alpha(T.text, 0.05) },
              "&.Mui-selected": {
                background: alpha(primary, isLight ? 0.08 : 0.14),
                color: primary,
                "&:hover": { background: alpha(primary, isLight ? 0.11 : 0.18) },
              },
              "&.Mui-disabled": { opacity: 0.45 },
            },
          },
        },

        MuiListItem: {
          styleOverrides: {
            root: { paddingTop: 2, paddingBottom: 2 },
          },
        },

        MuiChip: {
          styleOverrides: {
            root: {
              fontWeight: 500,
              borderRadius: 8,
              fontSize: "0.8125rem",
              transition: "all 0.15s ease",
              "&:not(.MuiChip-clickable):not(.MuiChip-deletable)": {
                pointerEvents: "none",
                userSelect: "none",
              },
            },
            filled: {
              background: alpha(T.text, 0.07),
              border: "1px solid var(--line-200)",
            },
            filledPrimary: {
              background: alpha(primary, 0.1),
              color: primary,
              border: `1px solid ${alpha(primary, 0.2)}`,
            },
            filledSuccess: {
              background: alpha(SUCCESS, 0.1),
              color: isLight ? "#15803D" : SUCCESS,
              border: `1px solid ${alpha(SUCCESS, 0.2)}`,
            },
            filledError: {
              background: alpha(DANGER, 0.1),
              color: isLight ? "#B91C1C" : "#FCA5A5",
              border: `1px solid ${alpha(DANGER, 0.2)}`,
            },
            filledWarning: {
              background: alpha(WARNING, 0.1),
              color: isLight ? "#B45309" : WARNING,
              border: `1px solid ${alpha(WARNING, 0.2)}`,
            },
            outlined: { borderColor: T.border },
          },
        },

        MuiTooltip: {
          defaultProps: { enterDelay: 400 },
          styleOverrides: {
            tooltip: {
              borderRadius: 8,
              fontSize: "0.75rem",
              fontWeight: 500,
              padding: "6px 10px",
              background: isLight ? "rgba(16,24,40,0.90)" : "rgba(248,250,252,0.92)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: isLight ? "#F8FAFC" : "#101828",
              border: `1px solid ${isLight ? "rgba(255,255,255,0.10)" : "rgba(16,24,40,0.10)"}`,
              boxShadow: "var(--shadow-card)",
            },
            arrow: {
              color: isLight ? "rgba(16,24,40,0.90)" : "rgba(248,250,252,0.92)",
            },
          },
        },

        MuiDivider: {
          styleOverrides: {
            root: { borderColor: T.border },
          },
        },

        MuiAppBar: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              backgroundImage: "none",
              background: isLight ? "rgba(247,248,252,0.92)" : "rgba(11,14,26,0.92)",
              backdropFilter: BLUR_SM,
              WebkitBackdropFilter: BLUR_SM,
              borderBottom: `1px solid ${T.border}`,
              boxShadow: `0 1px 2px ${alpha(isLight ? "#101828" : "#000", 0.05)}`,
            },
          },
        },

        MuiDrawer: {
          styleOverrides: {
            paper: {
              background: isLight ? "#F0EAFB" : "#13102B",
              border: "none",
              borderRight: `1px solid ${T.border}`,
            },
          },
        },

        MuiDialog: {
          styleOverrides: {
            paper: {
              background: isLight ? "rgba(255,255,255,0.97)" : "rgba(17,24,39,0.97)",
              backdropFilter: BLUR,
              WebkitBackdropFilter: BLUR,
              border: "1px solid var(--line-200)",
              boxShadow: "var(--shadow-card)",
              borderRadius: 20,
              "@media (prefers-reduced-transparency: reduce)": {
                background: "var(--surface-card)",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
              },
            },
          },
        },

        MuiDialogTitle: {
          styleOverrides: {
            root: {
              fontWeight: 700,
              fontSize: "1.0625rem",
              letterSpacing: "-0.01em",
              paddingBottom: 8,
            },
          },
        },

        MuiDialogActions: {
          styleOverrides: {
            root: { padding: "12px 24px", gap: 8 },
          },
        },

        MuiBackdrop: {
          styleOverrides: {
            root: {
              background: isLight ? "rgba(16,24,40,0.45)" : "rgba(0,0,0,0.65)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            },
            invisible: {
              background: "transparent",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            },
          },
        },

        MuiLinearProgress: {
          styleOverrides: {
            root: { borderRadius: 100, background: alpha(primary, 0.12) },
            bar:  { borderRadius: 100 },
          },
        },

        MuiCircularProgress: {
          defaultProps: { thickness: 3.5 },
        },

        MuiTableContainer: {
          styleOverrides: {
            root: {
              background: "var(--surface-card)",
              border: "1px solid var(--line-200)",
              boxShadow: "var(--shadow-card)",
              borderRadius: 18,
              overflow: "hidden",
            },
          },
        },

        MuiTableHead: {
          styleOverrides: {
            root: {
              "& .MuiTableCell-root": {
                background: isLight ? "#F7F8FC" : "#1E293B",
                fontWeight: 700,
                fontSize: "0.75rem",
                color: T.sub,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                borderBottom: `1px solid ${T.border}`,
              },
            },
          },
        },

        MuiTableRow: {
          styleOverrides: {
            root: {
              transition: "background 0.12s ease",
              "&:hover": {
                background: isLight ? alpha(PURPLE, 0.025) : alpha(PURPLE_L, 0.04),
              },
              "&:last-child td": { border: 0 },
            },
          },
        },

        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: `1px solid ${T.border}`,
              padding: "10px 14px",
              fontSize: "0.875rem",
            },
          },
        },

        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              minHeight: 42,
              borderRadius: 8,
              transition: "all 0.15s ease",
              "&.Mui-selected": { color: primary },
            },
          },
        },

        MuiTabs: {
          styleOverrides: {
            indicator: {
              height: 2.5,
              borderRadius: "2px 2px 0 0",
              background: `linear-gradient(90deg, ${TEAL}, ${primary})`,
            },
          },
        },

        MuiAccordion: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              background: "var(--surface-card)",
              border: "1px solid var(--line-200)",
              boxShadow: "none",
              borderRadius: "14px !important",
              marginBottom: 8,
              "&:before": { display: "none" },
              "&.Mui-expanded": { boxShadow: "none" },
            },
          },
        },

        MuiAccordionSummary: {
          styleOverrides: {
            root: {
              fontWeight: 600,
              "&.Mui-expanded": { minHeight: 48 },
            },
            content: { "&.Mui-expanded": { margin: "12px 0" } },
          },
        },

        MuiMenu: {
          styleOverrides: {
            paper: {
              background: isLight ? "rgba(255,255,255,0.97)" : "rgba(17,24,39,0.97)",
              backdropFilter: BLUR,
              WebkitBackdropFilter: BLUR,
              border: "1px solid var(--line-200)",
              boxShadow: "var(--shadow-card)",
              borderRadius: 14,
              "@media (prefers-reduced-transparency: reduce)": {
                background: "var(--surface-card)",
                backdropFilter: "none",
              },
            },
            list: { padding: "6px" },
          },
        },

        MuiMenuItem: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              fontSize: "0.875rem",
              fontWeight: 500,
              minHeight: 36,
              padding: "6px 10px",
              transition: "all 0.12s ease",
              "&:hover": { background: alpha(primary, 0.07) },
              "&.Mui-selected": {
                background: alpha(primary, 0.10),
                color: primary,
                "&:hover": { background: alpha(primary, 0.14) },
              },
            },
          },
        },

        MuiAlert: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              border: "1px solid",
              fontWeight: 500,
              fontSize: "0.875rem",
            },
            standardSuccess: {
              background: alpha(SUCCESS, 0.07),
              borderColor: alpha(SUCCESS, 0.2),
              color: isLight ? "#15803D" : "#4ADE80",
              "& .MuiAlert-icon": { color: SUCCESS },
            },
            standardError: {
              background: alpha(DANGER, 0.07),
              borderColor: alpha(DANGER, 0.2),
              color: isLight ? "#991B1B" : "#FCA5A5",
              "& .MuiAlert-icon": { color: DANGER },
            },
            standardWarning: {
              background: alpha(WARNING, 0.07),
              borderColor: alpha(WARNING, 0.2),
              "& .MuiAlert-icon": { color: WARNING },
            },
            standardInfo: {
              background: alpha("#3B82F6", 0.07),
              borderColor: alpha("#3B82F6", 0.2),
              "& .MuiAlert-icon": { color: "#3B82F6" },
            },
          },
        },

        MuiSnackbarContent: {
          styleOverrides: {
            root: {
              background: isLight ? "#1E293B" : "#F8FAFC",
              color: isLight ? "#F8FAFC" : "#101828",
              borderRadius: 12,
              fontWeight: 500,
              boxShadow: "var(--shadow-card)",
            },
          },
        },

        MuiBadge: {
          styleOverrides: {
            badge: {
              fontWeight: 700,
              fontSize: "0.6875rem",
              minWidth: 18,
              height: 18,
              padding: "0 4px",
              borderRadius: 9,
              boxShadow: `0 0 0 2px ${T.bg}`,
            },
          },
        },

        MuiSkeleton: {
          defaultProps: { animation: "wave" },
          styleOverrides: {
            root: {
              borderRadius: 8,
              background: isLight ? "rgba(16,24,40,0.06)" : "rgba(255,255,255,0.06)",
              "&::after": {
                background: isLight
                  ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.75), transparent)"
                  : "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)",
              },
            },
          },
        },

        MuiSwitch: {
          styleOverrides: {
            root: { padding: 6 },
            track: {
              borderRadius: 12,
              background: isLight ? "rgba(16,24,40,0.20)" : "rgba(255,255,255,0.15)",
              opacity: 1,
            },
            switchBase: {
              "&.Mui-checked + .MuiSwitch-track": {
                background: primary,
                opacity: 1,
              },
            },
          },
        },

        MuiCheckbox: {
          styleOverrides: {
            root: {
              "&.Mui-checked": { color: primary },
              "&:hover": { background: alpha(primary, 0.06) },
            },
          },
        },

        MuiRadio: {
          styleOverrides: {
            root: {
              "&.Mui-checked": { color: primary },
              "&:hover": { background: alpha(primary, 0.06) },
            },
          },
        },

        MuiStepper: {
          styleOverrides: { root: { padding: 0 } },
        },

        MuiStepLabel: {
          styleOverrides: {
            label: {
              fontWeight: 500,
              "&.Mui-active":    { fontWeight: 700, color: primary },
              "&.Mui-completed": { fontWeight: 600 },
            },
          },
        },

        MuiFab: {
          styleOverrides: {
            primary: {
              background: `linear-gradient(135deg, ${TEAL}, ${primary})`,
              boxShadow: `0 4px 16px ${alpha(primary, 0.4)}`,
              "&:hover": {
                background: `linear-gradient(135deg, #00A5A5, ${PURPLE_D})`,
                boxShadow: `0 6px 24px ${alpha(primary, 0.5)}`,
              },
            },
          },
        },

        MuiPagination: {
          styleOverrides: {
            root: {
              "& .MuiPaginationItem-root": { borderRadius: 8, fontWeight: 600 },
            },
          },
        },
      },
    });
  }, [mode, i18n.language]);

  document.body.dir = theme.direction;

  return (
    <ColorModeContext.Provider value={{ mode, toggleColorMode }}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </ColorModeContext.Provider>
  );
}
